/**
 * Centralized window global references.
 *
 * IMPORTANT: MyBuiltScripts.js loads AFTER ReactBundle.js.  Any global
 * defined by MyBuiltScripts.js must be resolved lazily (at call time),
 * not captured eagerly at module-load time, or it will be `undefined`.
 *
 * - Functions / constructors → use the _wg() wrapper so the lookup
 *   happens on every call.
 * - String / boolean constants defined before ReactBundle (e.g. in
 *   LibraryScripts.js or the HTML page itself) → safe to capture eagerly.
 * - String constants defined by MyBuiltScripts.js → import from
 *   their proper React-side module instead (e.g. @/constants/formats,
 *   @/utils/theme).
 */

const w = window as any;

/** Lazy window-global function wrapper — resolves the name at every call. */
const _wg = (name: string): ((...args: any[]) => any) =>
  (...args: any[]) => w[name](...args);

// ── Platform Detection ──────────────────────────────────────────────────
// These are set by native apps (UWP/iOS/Android) via global assignment.
// They must be read lazily: the legacy code declares them with `var`
// (so they appear on `window`), but native apps / setIOSApp() may set
// them after the React bundle first executes.  Using `export let` gives
// us live ES-module bindings that all importers see when updated.
export let isUWPApp: boolean = w.isUWPApp ?? false;
export let isIOSApp: boolean = w.isIOSApp ?? false;
export let isAndroidApp: boolean = w.isAndroidApp ?? false;
export let isWindowsAppSdkApp: boolean = w.isWindowsAppSdkApp ?? false;

// Re-read platform flags after window.onload (when native apps have
// finished injecting their globals and setIOSApp() has run).
if (typeof window !== 'undefined') {
  const refreshPlatformFlags = () => {
    isUWPApp = w.isUWPApp ?? false;
    isIOSApp = w.isIOSApp ?? false;
    isAndroidApp = w.isAndroidApp ?? false;
    isWindowsAppSdkApp = w.isWindowsAppSdkApp ?? false;
  };
  if (document.readyState === 'complete') {
    // Already loaded — refresh immediately.
    refreshPlatformFlags();
  } else {
    window.addEventListener('load', refreshPlatformFlags);
  }
}

// ── Third-party Libraries (loaded via LibraryScripts.js before ReactBundle) ─
export const Mousetrap: any = w.Mousetrap;
export const $: any = w.$;

// ── Functions from MyBuiltScripts.js (lazy) ─────────────────────────────
export const notifyApp = _wg('notifyApp');
export const kstConfirm = _wg('kstConfirm');
export const kstPrompt = _wg('kstPrompt');
export const deepCopyProperties = _wg('deepCopyProperties');
export const selectModuleCallback = _wg('selectModuleCallback');

// ── Date Format Constants ───────────────────────────────────────────────
// Re-exported from @/constants/formats for backward compatibility.
// Prefer importing directly from '@/constants/formats' in new code.
export {
  DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT,
  DATETIME_FORMAT_SERVER_UTC,
  DATE_FORMAT_DISPLAY_LOCAL_MOMENT,
  TIME_FORMAT_DISPLAY_LOCAL_MOMENT,
} from '@/constants/formats';

// ── Theme Constants ─────────────────────────────────────────────────────
// Re-exported from @/utils/theme for backward compatibility.
// Prefer importing directly from '@/utils/theme' in new code.
export {
  THEME_DAY_COOKIE_VALUE,
  THEME_NIGHT_BLUE_COOKIE_VALUE,
  THEME_NIGHT_GREY_COOKIE_VALUE,
} from '@/utils/theme';

// ── Legacy Entity Constructors (lazy) ───────────────────────────────────
// CallCard constructor is defined by MyBuiltScripts.js.
// Use: `new CallCard(result, moduleType, false)`
const _cls = (name: string): any => {
  const handler = {
    construct(_target: any, args: any[]) { return new w[name](...args); },
    apply(_target: any, _thisArg: any, args: any[]) { return w[name](...args); },
  };
  return new Proxy(function(){} as any, handler);
};
export const CallCard: any = _cls('CallCard');
